/**
 * Kiosk kabuğu: tam ekran, tek akış, geri dönüşü otomatik. Figma'nın demo kabuğundaki
 * kiosk/mobil değiştirici burada yok — kiosk tablette tek yüzeydir (ADR-006).
 */

import { useCallback, useEffect, useState } from 'react'
import KioskApp, { StepProgress, type KioskScreen } from './kiosk'
import KioskLock from './KioskLock'
import { C } from './ui'
import { KioskProvider } from './state/kiosk-store'
import { useKiosk } from './state/kiosk-context'

/** Son ekran misafirde kalmaz; attract'a döner ve oturum temizlenir (docs/api.md). */
const AUTO_RESET_MS = 6000

function KioskShell() {
  const [screen, setScreen] = useState<KioskScreen>(1)
  const { reset, streamConnected, eventId } = useKiosk()

  const navigate = useCallback(
    (next: KioskScreen) => {
      if (next === 1) reset()
      setScreen(next)
    },
    [reset],
  )

  useEffect(() => {
    if (screen !== 10) return
    const timer = setTimeout(() => navigate(1), AUTO_RESET_MS)
    return () => clearTimeout(timer)
  }, [screen, navigate])

  return (
    <div
      style={{
        width: '100vw',
        height: '100dvh',
        background: C.deepBlack,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Exo 2', sans-serif",
        overflow: 'hidden',
      }}
    >
      {screen !== 1 && (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            height: '60px',
            background: 'rgba(8,7,17,0.95)',
            borderBottom: '1px solid rgba(139,92,255,0.2)',
            backdropFilter: 'blur(12px)',
            flexShrink: 0,
            zIndex: 200,
            gap: '12px',
          }}
        >
          <button
            type="button"
            // Logo her ekranda görünür (screen 1 hariç) -- hangi ekranda olursa olsun tek
            // tıkla LANDING PAGE'e (screen 1) dönebilmek için (kullanıcı talebi, 2026-09-19:
            // "tüm sayfalarda ... hangi sayfada basarsam kesinlikle LANDING PAGE ana
            // sayfaya dönmeli"). navigate(1) zaten reset()'i de tetikliyor -- attract'a
            // dönüşle aynı davranış, misafirin yarım kalmış oturumu böyle temizlenir.
            onClick={() => navigate(1)}
            aria-label="Ana sayfaya dön"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0,
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              font: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${C.primaryPurple}, ${C.neonPurple})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 0 14px ${C.neonPurple}77`,
                fontSize: '16px',
              }}
            >
              🎮
            </div>
            <div>
              <div
                style={{
                  fontWeight: 900,
                  fontSize: '14px',
                  color: C.white,
                  letterSpacing: '0.12em',
                }}
              >
                CHAR<span style={{ color: C.neonPurple }}>GEN</span>
              </div>
              <div style={{ fontSize: '9px', color: C.secText, letterSpacing: '0.1em' }}>
                {eventId ?? '—'}
              </div>
            </div>
          </button>

          <StepProgress screen={screen} />

          {/* Operatör göstergesi: canlı olay akışı var mı — açıklama yok, tek nokta */}
          <div
            title={streamConnected ? 'canlı' : 'bağlantı yok'}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              flexShrink: 0,
              background: streamConnected ? '#00E676' : '#F0446C',
              boxShadow: `0 0 8px ${streamConnected ? '#00E676' : '#F0446C'}`,
            }}
          />
        </header>
      )}

      <main style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <KioskApp screen={screen} onNavigate={navigate} />
      </main>
    </div>
  )
}

export default function App() {
  return (
    <KioskLock>
      <KioskProvider>
        <KioskShell />
      </KioskProvider>
    </KioskLock>
  )
}
